import { useRoute, useRouter } from 'vue-router';
import { HOME_ROUTE } from '@app/router/router';
import { useFlowProgress } from '@app/composables/useFlowProgress';

/**
 * App-specific navigation helpers.
 *
 * Provides convenient methods for navigating to common routes in the app.
 * This is app-specific business logic, not generic infrastructure.
 */
export function useNavigation() {
  const route = useRoute();
  const router = useRouter();

  const goHome = () => router.push(HOME_ROUTE);

  /**
   * Replace-navigate home: used by end-of-flow completion CTAs so the finished
   * flow isn't re-enterable via the back button.
   */
  const goHomeReplace = () => router.replace(HOME_ROUTE);

  const goBack = () => router.back();

  /**
   * Semantic "up": pop a real history entry when one exists, else fall back to
   * the route's declared `meta.parent` (or HOME_ROUTE). No-ops while a blocking
   * flow animation is running.
   */
  const goUp = () => {
    if (useFlowProgress().locked.value) return;
    if (router.options.history.state.back != null) {
      router.back();
    } else {
      router.push((route.meta.parent as string | undefined) ?? HOME_ROUTE);
    }
  };

  return {
    goHome,
    goHomeReplace,
    goBack,
    goUp,
  };
}
